import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Thermometer, Trash2, CalendarClock, ClipboardCheck, Package, X, Loader2, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

export default function KitchenModePage() {
  const { locationId } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("temps");
  const [temps, setTemps] = useState([]);
  const [tempValues, setTempValues] = useState({});
  const [products, setProducts] = useState([]);
  const [expirations, setExpirations] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [running, setRunning] = useState(null);
  const [results, setResults] = useState({});
  const [wasteForm, setWasteForm] = useState({ product_id: "", quantity: "", reason: "scadenza" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = { location_id: locationId };
    api.get("/temperatures/latest", { params }).then((r) => setTemps(r.data)).catch(() => {});
    api.get("/products", { params }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/expirations", { params }).then((r) => setExpirations(r.data)).catch(() => {});
    api.get("/haccp/templates", { params }).then((r) => setTemplates(r.data)).catch(() => {});
  }, [locationId]);

  useEffect(() => { load(); }, [load]);

  const submitTemp = async (eq) => {
    const v = tempValues[eq.equipment_id];
    if (v === "" || v == null) return;
    try {
      const r = await api.post("/temperatures", { equipment_id: eq.equipment_id, temperature: Number(v), location_id: locationId });
      if (r.data.out_of_range) toast.error(`${eq.name}: ${v}°C ⚠`, { duration: 5000 });
      else toast.success(`${eq.name}: ${v}°C`);
      setTempValues({ ...tempValues, [eq.equipment_id]: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitWaste = async () => {
    setBusy(true);
    try {
      await api.post("/waste", { ...wasteForm, quantity: Number(wasteForm.quantity), location_id: locationId });
      toast.success(t("common.success"));
      setWasteForm({ product_id: "", quantity: "", reason: "scadenza" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const quickMove = async (p, delta) => {
    try {
      await api.post("/inventory/movement", {
        product_id: p.product_id, type: delta > 0 ? "load" : "unload",
        quantity: Math.abs(delta), location_id: locationId,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeCheck = async () => {
    setBusy(true);
    try {
      await api.post("/haccp/checks", {
        template_id: running.template_id,
        results: running.items.map((it) => ({ item: it, done: results[it]?.done || false, note: "" })),
        location_id: locationId,
      });
      toast.success(t("common.success"));
      setRunning(null);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const TABS = [
    { key: "temps", icon: Thermometer, label: t("kitchen.temps"), tid: "km-tab-temps" },
    { key: "waste", icon: Trash2, label: t("kitchen.waste"), tid: "km-tab-waste" },
    { key: "expirations", icon: CalendarClock, label: t("kitchen.expirations"), tid: "km-tab-expirations" },
    { key: "haccp", icon: ClipboardCheck, label: t("kitchen.haccp"), tid: "km-tab-haccp" },
    { key: "stock", icon: Package, label: t("kitchen.stock"), tid: "km-tab-stock" },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-5 md:pl-14" data-testid="kitchen-mode-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold">{t("kitchen.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("kitchen.subtitle")}</p>
        </div>
        <Button data-testid="exit-kitchen-mode" variant="outline" onClick={() => navigate("/dashboard")} className="h-12 tactile-btn">
          <X className="h-4 w-4 mr-1" />{t("kitchen.exit")}
        </Button>
      </div>

      <div className="grid grid-cols-5 gap-2" data-testid="km-tabs">
        {TABS.map(({ key, icon: Icon, label, tid }) => (
          <button key={key} data-testid={tid} onClick={() => { setTab(key); setRunning(null); }}
            className={`tactile-btn rounded-2xl border-2 p-3 flex flex-col items-center gap-1.5 min-h-20 justify-center ${
              tab === key ? "bg-slate-900 text-white border-slate-900" : "bg-card"
            }`}>
            <Icon className="h-6 w-6" />
            <span className="text-[10px] font-bold uppercase leading-tight text-center">{label}</span>
          </button>
        ))}
      </div>

      {tab === "temps" && (
        <div className="space-y-3" data-testid="km-temps">
          {temps.map(({ equipment: eq, latest }) => (
            <div key={eq.equipment_id} className="bg-card border rounded-2xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-display font-bold text-lg">{eq.name}</p>
                {latest && (
                  <p className={`font-mono font-bold ${latest.out_of_range ? "text-red-600" : "text-emerald-600"}`}>
                    {latest.temperature > 0 ? "+" : ""}{latest.temperature}°C
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Input data-testid={`km-temp-input-${eq.equipment_id}`} type="number" step="0.1" placeholder="°C"
                  value={tempValues[eq.equipment_id] || ""}
                  onChange={(e) => setTempValues({ ...tempValues, [eq.equipment_id]: e.target.value })}
                  className="h-16 text-3xl font-mono text-center flex-1" />
                <Button data-testid={`km-temp-submit-${eq.equipment_id}`} onClick={() => submitTemp(eq)}
                  className="h-16 px-8 text-lg tactile-btn font-bold bg-cyan-600 hover:bg-cyan-700">OK</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "waste" && (
        <div className="bg-card border rounded-2xl p-5 space-y-4" data-testid="km-waste">
          <select data-testid="km-waste-product" value={wasteForm.product_id}
            onChange={(e) => setWasteForm({ ...wasteForm, product_id: e.target.value })}
            className="h-14 w-full rounded-xl border bg-background px-3 text-base">
            <option value="">— {t("waste.product")} —</option>
            {products.filter((p) => p.current_qty > 0).map((p) => (
              <option key={p.product_id} value={p.product_id}>{p.name} ({p.current_qty} {p.unit})</option>
            ))}
          </select>
          <Input data-testid="km-waste-qty" type="number" step="0.01" min="0.01" placeholder={t("products.quantity")}
            value={wasteForm.quantity} onChange={(e) => setWasteForm({ ...wasteForm, quantity: e.target.value })}
            className="h-16 text-3xl font-mono text-center" />
          <select data-testid="km-waste-reason" value={wasteForm.reason}
            onChange={(e) => setWasteForm({ ...wasteForm, reason: e.target.value })}
            className="h-14 w-full rounded-xl border bg-background px-3 text-base">
            {Object.keys(t("waste.reasons")).map((r) => <option key={r} value={r}>{t(`waste.reasons.${r}`)}</option>)}
          </select>
          <Button data-testid="km-waste-submit" onClick={submitWaste}
            disabled={busy || !wasteForm.product_id || !wasteForm.quantity}
            className="w-full h-16 text-lg bg-orange-600 hover:bg-orange-700 tactile-btn font-bold">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("waste.log")}
          </Button>
        </div>
      )}

      {tab === "expirations" && expirations && (
        <div className="space-y-3" data-testid="km-expirations">
          {[...expirations.expired, ...expirations.today, ...expirations.tomorrow].map((p) => (
            <div key={p.product_id} className={`border-2 rounded-2xl p-4 flex items-center gap-3 ${p.days_left <= 0 ? "bg-red-50 border-red-300" : "bg-amber-50 border-amber-300"}`}>
              <CalendarClock className="h-6 w-6 shrink-0" />
              <div className="flex-1">
                <p className="font-bold">{p.name}</p>
                <p className="text-xs font-mono">{p.expiry_date}</p>
              </div>
              <p className="font-mono font-bold">{p.current_qty} {p.unit}</p>
            </div>
          ))}
          {![...expirations.expired, ...expirations.today, ...expirations.tomorrow].length && (
            <p className="text-center text-muted-foreground py-10">{t("exp.noItems")}</p>
          )}
        </div>
      )}

      {tab === "haccp" && !running && (
        <div className="space-y-3" data-testid="km-haccp">
          {templates.map((tpl) => (
            <button key={tpl.template_id} data-testid={`km-run-${tpl.template_id}`}
              onClick={() => { setRunning(tpl); setResults({}); }}
              className="tactile-btn w-full bg-card border rounded-2xl p-5 flex items-center gap-4 text-left">
              <ClipboardCheck className="h-8 w-8 text-violet-600 shrink-0" />
              <div>
                <p className="font-display font-bold text-lg">{tpl.name}</p>
                <p className="text-xs text-muted-foreground">{tpl.items.length} {t("haccp.templates").toLowerCase()} · ~2 min</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {tab === "haccp" && running && (
        <div className="space-y-3" data-testid="km-haccp-run">
          <h2 className="font-display font-bold text-xl">{running.name}</h2>
          {running.items.map((item, i) => {
            const done = results[item]?.done;
            return (
              <button key={i} data-testid={`km-haccp-item-${i}`}
                onClick={() => setResults({ ...results, [item]: { done: !done } })}
                className={`tactile-btn w-full border-2 rounded-2xl p-4 flex items-center gap-3 text-left ${done ? "bg-emerald-50 border-emerald-300" : "bg-card"}`}>
                {done ? <CheckCircle2 className="h-8 w-8 text-emerald-600 shrink-0" /> : <Circle className="h-8 w-8 text-muted-foreground shrink-0" />}
                <span className="font-semibold text-lg">{item}</span>
              </button>
            );
          })}
          <div className="flex gap-2">
            <Button data-testid="km-haccp-complete" onClick={completeCheck} disabled={busy}
              className="flex-1 h-16 text-lg bg-emerald-600 hover:bg-emerald-700 tactile-btn font-bold">
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("haccp.complete")}
            </Button>
            <Button data-testid="km-haccp-cancel" variant="outline" onClick={() => setRunning(null)} className="h-16">{t("common.cancel")}</Button>
          </div>
        </div>
      )}

      {tab === "stock" && (
        <div className="space-y-2" data-testid="km-stock">
          {products.map((p) => (
            <div key={p.product_id} className="bg-card border rounded-2xl p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{p.name}</p>
                <p className={`font-mono text-sm font-bold ${p.current_qty <= p.min_qty ? "text-red-600" : "text-emerald-700"}`}>
                  {p.current_qty} {p.unit}
                </p>
              </div>
              <button data-testid={`km-minus-${p.product_id}`} onClick={() => quickMove(p, -1)}
                className="tactile-btn h-14 w-14 rounded-2xl bg-sky-600 text-white text-2xl font-bold flex items-center justify-center">−1</button>
              <button data-testid={`km-plus-${p.product_id}`} onClick={() => quickMove(p, 1)}
                className="tactile-btn h-14 w-14 rounded-2xl bg-emerald-600 text-white text-2xl font-bold flex items-center justify-center">+1</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
